import React from 'react';
import FormField from './FormField';
import green_check_icon from 'src/images/icon/green_check_icon.png';
import { ERROR_MESSAGES } from 'src/constants/ErrorMessages';
import { isValidEmail } from 'src/modules/FormValidators';
import {PENDING, VERIFIED, INCORRECT_CODE, THROTTLED, EXPIRED_CODE} from "src/constants/EmailVerificationStatus";
import { verifyEmail } from 'src/backend/VerifyEmailCaller';
import 'src/assets/emailVerificationModal.scss';

interface EmailVerificationFieldProps {
    label: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    setFormData: Function;
    emailError: boolean;
    emailVerified: boolean;
    consentConfirmed: boolean;
    infoMessage?: string;
    placeholder?: string;
}

const EmailVerificationField: React.FC<EmailVerificationFieldProps> = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    setFormData,
    emailError,
    emailVerified,
    consentConfirmed,
    infoMessage,
    placeholder
}) => {
    const [showModal, setShowModal] = React.useState(false);
    const [verificationCode, setVerificationCode] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');
    const [infoMsg, setInfoMsg] = React.useState('');
    const [successMessage, setSuccessMessage] = React.useState('');
    const [cooldownEnd, setCooldownEnd] = React.useState<number | null>(null);
    const [timeRemaining, setTimeRemaining] = React.useState(0);
    const [submitting, setSubmitting] = React.useState(false);

    const updateVerificationState = (verified: boolean) => {
        setFormData((prev: any) => ({ ...prev, emailVerified: verified }));
        setShowModal(!verified);
    };

    const startCooldown = () => {
        const endTime = Date.now() + 180000; // 3 minutes
        setCooldownEnd(endTime);
    };

    React.useEffect(() => {
        if (!cooldownEnd) return;
        
        const interval = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
            setTimeRemaining(remaining);
            
            if (remaining === 0) {
                setCooldownEnd(null);
                clearInterval(interval);
            }
        }, 1000);
        
        return () => clearInterval(interval);
    }, [cooldownEnd]);

    const isCooldownActive = cooldownEnd && Date.now() < cooldownEnd;

    const handleResendEmail = async () => {
        if (isCooldownActive) return;
        startCooldown();
        await handleVerifyEmail();
        setSuccessMessage('Verification code sent successfully, please check your mailbox and try again.');
        setErrorMessage('');
    };

    const handleVerifyEmail = async (code?: string) => {
        // If verify email or continue was pressed, code should always be reset.
        setVerificationCode('');

        // Email already verified - do not try to verify again.
        if (emailVerified) {
            return;
        }

        // The first time the verify button was pressed - begin cooldown.
        if (!code && !isCooldownActive) {
            startCooldown();
        }

        // If user is re-opening the verification modal while cooldown is active, let them.
        if (isCooldownActive && !code) {
            setShowModal(true);
        }

        if (code || !isCooldownActive) {
            setSubmitting(true);

            try {
                const params = code ? { email: value, verificationCode: code } : { email: value };
                const data = await verifyEmail(params);
                const verificationStatus = data.outcome;

                switch (verificationStatus) {
                    case VERIFIED:
                        setErrorMessage('');
                        updateVerificationState(true);
                        break;
                    case PENDING:
                        setInfoMsg('Please check your mailbox and enter the received verification code.');
                        setErrorMessage('');
                        updateVerificationState(false);
                        break;
                    case INCORRECT_CODE:
                        setErrorMessage('Incorrect code. Please try again.');
                        setInfoMsg('');
                        updateVerificationState(false);
                        break;
                    case THROTTLED:
                        setErrorMessage('Unable to verify your email due to multiple failed attempts. Please contact amazon-autos-dealer-inquiry@amazon.com');
                        updateVerificationState(false);
                        break;
                    case EXPIRED_CODE:
                        setErrorMessage('The verification code has expired, please request a new one below');
                        updateVerificationState(false);
                        break;
                    default:
                        console.error('Unknown verification status:', verificationStatus);
                        setErrorMessage('An error occurred during verification');
                        updateVerificationState(false);
                        break;
                }
            } catch (error) {
                console.error('Email verification failed:', error);
                setErrorMessage('An error occurred. Please try again later.');
                updateVerificationState(false);
            }

            setSubmitting(false);
        }
    };
    return (
        <>
        {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                    <h2>Enter your code</h2>
                    <p>We sent the code to <strong>{value}</strong> in order to verify the email account. It may take a few minutes to receive the email.</p>
                    <input
                        type="text"
                        className="verification-code-input"
                        placeholder="Enter code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                    />
                    {infoMsg && <p className="info-message">{infoMsg}</p>}
                    {errorMessage && <p className="error-message">{errorMessage}</p>}
                    <button type="button" className="modal-continue-button" disabled={(!verificationCode || submitting)} onClick={() => { setSuccessMessage(''); handleVerifyEmail(verificationCode); }}>
                        {submitting ? (
                            <span className="button-content">
                                <span className="spinner"></span>
                                Submitting...
                            </span>
                        ) : (
                            "Continue"
                        )}
                    </button>
                    <p>Didn't get the email? {isCooldownActive ? <span>Wait {timeRemaining}s to resend</span> : <a href="#" onClick={(e) => { e.preventDefault(); handleResendEmail(); }}>Resend email</a>}</p>
                    {successMessage && <p className="success-message">{successMessage}</p>}
                    <p>Need help? <a href="mailto:amazon-autos-dealer-inquiry@amazon.com">amazon-autos-dealer-inquiry@amazon.com</a></p>
                </div>
            </div>
        )}
        <div className="email-verification-row">
            <FormField
                label={label}
                name={name}
                type="email"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                infoMessage={infoMessage}
                placeholder={placeholder}
                error={emailError}
                errorMessage={value.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_EMAIL}
            />
            <div className="verification-section">
                {!emailVerified && (
                    <button type="button" className="verify-email-button" onClick={() => handleVerifyEmail()} disabled={!isValidEmail(value)}>
                        Verify email
                    </button>
                )}
                {emailVerified ? (
                    <span className="verification-success">
                        <img src={green_check_icon} alt="" />
                        Verification completed
                    </span>
                ) : (
                    consentConfirmed && isValidEmail(value) && (
                        <span className="field-error">{ERROR_MESSAGES.EMAIL_NOT_VERIFIED}</span>
                    )
                )}
            </div>
        </div>
        </>
    );
};

export default EmailVerificationField;
